// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a picture a Markdown document shows (`![logo](docs/logo.png)`, a README's badges, a
// screenshot in a pull request's description). Konvo can't load what a document names by itself
// (the files are on the agent's machine, web pictures must come through the media proxy), so the
// document's source hands over an address the app may show. Without one, or when it fails, the
// picture's caption stands in for it. Pictures are asked for as they come near the screen, and keep
// their written size's shape while they load. With `open`, a tap opens the picture in the media
// viewer with the document's other pictures (MarkdownDocument puts them in one MediaGallery).

import styles from '@app/features/document_preview/DocumentPreview.module.css';
import {useNearViewport} from '@app/features/messaging/hooks/useNearViewport';
import type {MediaViewerFile} from '@app/features/ui/commands/MediaViewerCommands';
import {useMediaGalleryItem} from '@app/features/ui/components/MediaGallery';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

/** An address the app may show for a picture a document names (`src` as written), or null when there's none. */
export type MarkdownPictureLoader = (src: string, signal: AbortSignal) => Promise<string | null>;

const isData = (src: string) => /^data:image\//i.test(src);

/** What the viewer calls a picture: its caption, else the file name in its address (`shot.png`). */
export function pictureName(src: string, alt: string): string {
	if (alt) return alt;
	try {
		const last = decodeURIComponent(new URL(src, 'https://x.invalid/').pathname.split('/').pop() || '');
		// An upload's id (github.com/user-attachments/assets/<id>) is no name.
		if (/\.[a-z0-9]{2,5}$/i.test(last)) return last;
	} catch {}
	return 'image';
}

export function DocumentPicture({
	src,
	alt,
	width,
	height,
	load,
	open = false,
}: {
	src: string;
	alt: string;
	width?: number;
	height?: number;
	load?: MarkdownPictureLoader;
	/** A tap opens it in the media viewer (not for a picture inside a link, which follows the link). */
	open?: boolean;
}) {
	const {t} = useLingui();
	// undefined while it loads, null when there's nothing to show.
	const [url, setUrl] = useState<string | null | undefined>(() => (isData(src) ? src : load ? undefined : null));
	const {ref: nearRef, isNearViewport} = useNearViewport<HTMLElement>({disabled: !load || isData(src)});
	const image = useRef<HTMLImageElement | null>(null);

	useEffect(() => {
		if (isData(src) || !load) {
			setUrl(isData(src) ? src : null);
			return;
		}
		if (!isNearViewport) return;
		const controller = new AbortController();
		// The picture shown stays until its replacement is known, so a re-render never flickers.
		load(src, controller.signal)
			.then((value) => {
				if (!controller.signal.aborted) setUrl(value);
			})
			.catch(() => {
				if (!controller.signal.aborted) setUrl(null);
			});
		return () => controller.abort();
	}, [src, load, isNearViewport]);

	const name = pictureName(src, alt);
	const file = (address: string): MediaViewerFile => {
		const drawn = image.current?.src === address && image.current.complete ? image.current : null;
		return {
			src: address,
			filename: name,
			kind: 'image',
			width: drawn?.naturalWidth || null,
			height: drawn?.naturalHeight || null,
		};
	};
	// In the gallery while it loads, too: stepping to a picture further down reads it then.
	const picture = useMediaGalleryItem(
		open && url !== null && (url || load)
			? () =>
					url
						? [file(url)]
						: (load?.(src, new AbortController().signal) ?? Promise.resolve(null))
								.catch(() => null)
								.then((address) => (address ? [file(address)] : []))
			: null,
	);
	const ref = useCallback(
		(element: HTMLElement | null) => {
			nearRef(element);
			picture.ref(element);
		},
		[nearRef, picture.ref],
	);

	if (url === null) {
		const caption = alt || src.split(/[\\/]/).pop() || src;
		return <span className={styles.pictureCaption}>{caption}</span>;
	}
	// The written size's shape while it loads, so the text below doesn't jump when it arrives.
	const pendingSize: React.CSSProperties =
		width && height
			? {width: `min(${width}px, 100%)`, aspectRatio: `${width} / ${height}`}
			: {width: Math.min(width ?? 88, 640), height: Math.min(height ?? (width ? width / 4 : 20), 240)};
	const drawn =
		url === undefined ? (
			<span
				ref={open ? undefined : ref}
				className={styles.picturePending}
				role="img"
				aria-label={alt}
				aria-busy="true"
				style={pendingSize}
			/>
		) : (
			<img
				ref={(element) => {
					image.current = element;
					if (!open) ref(element);
				}}
				className={styles.picture}
				src={url}
				alt={alt}
				width={width}
				height={height}
				// The width it was given is how wide it is drawn, as on GitHub (never wider than the page).
				style={width ? {width} : undefined}
				loading="lazy"
				decoding="async"
				draggable={false}
				onError={() => setUrl(null)}
			/>
		);
	if (!open) return drawn;
	return (
		<button
			ref={ref}
			type="button"
			className={styles.pictureButton}
			// A tap opens the picture, not whatever the document sits in (a task card opens its editor).
			onClick={(event) => {
				event.stopPropagation();
				picture.open();
			}}
			aria-label={t({
				message: `Open ${name}`,
				comment:
					'Document preview: accessible label of a picture in a document (such as a pull request description) that opens in the media viewer. {name} is the picture’s caption or file name.',
			})}
			data-flx="document-preview.picture.open"
			data-picture-src={src}
		>
			{drawn}
		</button>
	);
}
